/**
 * VIN decoding via NHTSA vPIC — the federal database. Free, no key, no quota.
 *
 * What it gives you reliably: year, make, model, body class, cylinders,
 * displacement, fuel type, doors, and usually drive type.
 *
 * What it gives you unreliably: trim. vPIC returns manufacturer series codes
 * rather than marketing names, so where DealerCenter showed "328i Sedan 4D"
 * this may return "3-Series" or nothing at all. Transmission detail and curb
 * weight are frequently blank too. Those fields stay editable and the app
 * remembers what you type — see rememberTrim below.
 */

import { validateVin } from "./vin";

const VPIC = "https://vpic.nhtsa.dot.gov/api/vehicles/DecodeVinValues";

/** Set VITE_VIN_PROXY only if the browser blocks the direct call on CORS.
    See worker/vin-proxy.js. */
const PROXY = import.meta.env.VITE_VIN_PROXY as string | undefined;

export interface DecodedVehicle {
  year: number | null;
  make: string;
  model: string;
  trim: string;
  bodyType: string;
  doors: string;
  cylinders: string;
  engine: string;
  transmission: string;
  drivetrain: string;
  fuelType: string;
  weight: string;
  /** Fields vPIC left blank, so the UI can say what still needs typing. */
  missing: string[];
}

export type DecodeResult =
  | { ok: true; vehicle: DecodedVehicle; note?: string }
  | { ok: false; message: string };

/* ------------------------------------------------------------------ */
/* Year from the VIN itself                                            */
/* ------------------------------------------------------------------ */

const YEAR_CODES = "ABCDEFGHJKLMNPRSTVWXY123456789";

/**
 * Position 10 encodes model year on a 30-year cycle. Position 7 disambiguates:
 * alphabetic means 2010 or later, numeric means 1980 to 2009. Used as a
 * fallback when vPIC returns no ModelYear, and as a cross-check when it does.
 */
export function yearFromVin(vin: string): number | null {
  if (vin.length !== 17) return null;
  const idx = YEAR_CODES.indexOf(vin[9]!);
  if (idx < 0) return null;
  const early = 1980 + idx;
  const late = early + 30;
  const modern = /[A-Z]/.test(vin[6]!);
  const year = modern ? late : early;
  return year > new Date().getFullYear() + 1 ? early : year;
}

/* ------------------------------------------------------------------ */
/* Decode                                                              */
/* ------------------------------------------------------------------ */

/** vPIC uses "" and "Not Applicable" for unknown. Normalise both to "". */
function clean(v: unknown): string {
  const s = String(v ?? "").trim();
  if (!s || /^(not applicable|n\/?a|unknown|null)$/i.test(s)) return "";
  return s;
}

/** Makes that are acronyms or stylised, which title casing would mangle. */
const MAKE_AS_IS = new Set([
  "BMW", "GMC", "RAM", "MINI", "MG", "SRT", "FIAT", "AM GENERAL",
  "KTM", "BYD", "INEOS", "VPG", "SSR",
]);

/**
 * vPIC returns makes in caps. Title casing them gives "Bmw" and
 * "Mercedes-benz", both of which look wrong on a printed form.
 */
function titleCase(s: string): string {
  const upper = s.toUpperCase();
  if (MAKE_AS_IS.has(upper)) return upper;
  return s
    .toLowerCase()
    .replace(/(^|[\s\-/])(\w)/g, (_m, sep: string, ch: string) => sep + ch.toUpperCase());
}

/** Build a readable engine string from the separate fields vPIC returns. */
function engineText(r: Record<string, unknown>): string {
  const cyl = clean(r.EngineCylinders);
  const litres = clean(r.DisplacementL);
  const turbo = /turbo/i.test(clean(r.Turbo)) ? "Turbo" : "";
  const parts: string[] = [];
  if (cyl) parts.push(`${cyl}-Cyl`);
  if (turbo) parts.push(turbo);
  if (litres) parts.push(`${Number(litres).toFixed(1)}L`);
  return parts.join(" ");
}

function transmissionText(r: Record<string, unknown>): string {
  const style = clean(r.TransmissionStyle);
  const speeds = clean(r.TransmissionSpeeds);
  if (style && speeds) return `${style}, ${speeds}-Spd`;
  return style || (speeds ? `${speeds}-Spd` : "");
}

export async function decodeVin(
  rawVin: string,
  signal?: AbortSignal,
): Promise<DecodeResult> {
  const check = validateVin(rawVin);
  if (!check.ok) return { ok: false, message: check.message };
  const vin = check.vin;

  const guessedYear = yearFromVin(vin);
  const yearParam = guessedYear ? `&modelyear=${guessedYear}` : "";
  const url = PROXY
    ? `${PROXY.replace(/\/$/, "")}/?vin=${vin}${yearParam}`
    : `${VPIC}/${encodeURIComponent(vin)}?format=json${yearParam}`;

  let payload: { Results?: Record<string, unknown>[] };
  try {
    const res = await fetch(url, { signal });
    if (!res.ok) {
      return {
        ok: false,
        message: `The federal VIN service returned ${res.status}. Enter the details by hand.`,
      };
    }
    payload = await res.json();
  } catch (err) {
    if (err instanceof DOMException && err.name === "AbortError") {
      return { ok: false, message: "" };   // superseded by a newer lookup
    }
    return {
      ok: false,
      message: "Couldn't reach the federal VIN service. Enter the details by hand.",
    };
  }

  const r = payload.Results?.[0];
  if (!r) return { ok: false, message: "No result for that VIN. Enter the details by hand." };

  // ErrorCode is a comma-separated list. "0" means clean; anything containing
  // 11 means the VIN was not found in the database at all.
  const codes = clean(r.ErrorCode).split(",").map((c) => c.trim());
  const notFound = codes.includes("11") || codes.includes("400");

  const year = Number(clean(r.ModelYear)) || guessedYear;
  const make = titleCase(clean(r.Make));
  const model = clean(r.Model);
  // Trim is the weak field. Series is often the better of the two.
  const trim = clean(r.Trim) || clean(r.Series) || clean(r.Trim2);

  if (notFound && !make) {
    return {
      ok: false,
      message: "The federal database doesn't have this VIN. Enter the details by hand.",
    };
  }

  const vehicle: DecodedVehicle = {
    year: year ?? null,
    make,
    model,
    trim,
    bodyType: clean(r.BodyClass),
    doors: clean(r.Doors),
    cylinders: clean(r.EngineCylinders),
    engine: engineText(r),
    transmission: transmissionText(r),
    drivetrain: clean(r.DriveType),
    fuelType: clean(r.FuelTypePrimary),
    weight: clean(r.CurbWeightLB),
    missing: [],
  };

  vehicle.missing = (
    [
      ["trim", vehicle.trim],
      ["transmission", vehicle.transmission],
      ["body type", vehicle.bodyType],
    ] as const
  )
    .filter(([, v]) => !v)
    .map(([k]) => k);

  const note =
    !clean(r.ModelYear) && guessedYear
      ? "Year read from the VIN itself; check it."
      : undefined;

  return { ok: true, vehicle, note };
}

/* ------------------------------------------------------------------ */
/* Trim memory                                                         */
/* ------------------------------------------------------------------ */
/**
 * vPIC rarely knows the marketing trim. Rather than make you retype
 * "328i Sedan 4D" for every BMW 3-Series, remember what was entered for a
 * given year/make/model and offer it next time. Stored locally: it is a
 * convenience, not a record, and does not belong in the deal data.
 */

const KEY = "frontlot.trims.v1";

const trimKey = (year: number, make: string, model: string) =>
  `${year}|${make}|${model}`.toLowerCase();

export function rememberTrim(year: number, make: string, model: string, trim: string) {
  const t = trim.trim();
  if (!t) return;
  try {
    const all = JSON.parse(localStorage.getItem(KEY) ?? "{}") as Record<string, string>;
    all[trimKey(year, make, model)] = t;
    localStorage.setItem(KEY, JSON.stringify(all));
  } catch {
    // localStorage unavailable in private mode; the feature is optional.
  }
}

export function recallTrim(year: number, make: string, model: string): string {
  try {
    const all = JSON.parse(localStorage.getItem(KEY) ?? "{}") as Record<string, string>;
    return all[trimKey(year, make, model)] ?? "";
  } catch {
    return "";
  }
}

/* ------------------------------------------------------------------ */
/* Is this a real VIN?                                                  */
/* ------------------------------------------------------------------ */

/**
 * Ask vPIC whether a VIN decodes cleanly, and nothing else.
 *
 * Used by the scanner to settle a tie. When a camera misreads a character,
 * more than one repair can pass the check digit — WDC0GAKB2KV146451 read off
 * a real label yields both WDCDGAKB2KV146451 and WDC0G4KB2KV146451, and the
 * check digit cannot tell you which car is in front of you. The federal
 * database can: the first comes back "VIN has errors in few positions" with
 * no model, the second comes back clean as a 2019 GLC-Class.
 *
 * Returns false on any doubt, including a network failure, because the caller
 * falls back to asking a person and that is the safe direction to fail in.
 */
export async function vinDecodesClean(
  vin: string,
  signal?: AbortSignal,
): Promise<boolean> {
  if (!validateVin(vin).ok) return false;

  const year = yearFromVin(vin);
  const url = PROXY
    ? `${PROXY.replace(/\/$/, "")}/?vin=${vin}${year ? `&modelyear=${year}` : ""}`
    : `${VPIC}/${encodeURIComponent(vin)}?format=json${year ? `&modelyear=${year}` : ""}`;

  try {
    const res = await fetch(url, { signal });
    if (!res.ok) return false;
    const payload = await res.json() as { Results?: Record<string, unknown>[] };
    const r = payload.Results?.[0];
    if (!r) return false;
    // ErrorCode "0" on its own is the only clean answer. Anything else means
    // vPIC could not account for every character.
    const codes = clean(r.ErrorCode).split(",").map((c) => c.trim()).filter(Boolean);
    const spotless = codes.length === 1 && codes[0] === "0";
    // A make with no model is what a near-miss VIN looks like: enough of the
    // WMI resolves to name the manufacturer, and nothing after it does.
    return spotless && !!clean(r.Make) && !!clean(r.Model);
  } catch {
    return false;
  }
}
